import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { BrandMark } from '../components/layout/BrandMark';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { signUp } from '../lib/auth-client';

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signUp.email({ name, email, password });
      if (result.error) {
        setError(result.error.message ?? 'Registrering misslyckades.');
      } else {
        void navigate({ to: '/' });
      }
    } catch {
      setError('Något gick fel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <BrandMark />
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
          <h1 className="text-2xl font-semibold tracking-tight">Skapa konto</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Kom igång med en lugnare koll på pengarna.
          </p>
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="register-name">Namn</Label>
              <Input
                id="register-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                className="h-10"
                placeholder="Ditt namn"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email">E-postadress</Label>
              <Input
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-10"
                placeholder="din@email.se"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password">Lösenord</Label>
              <Input
                id="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                className="h-10"
                placeholder="Minst 8 tecken"
              />
            </div>
            {error && (
              <p
                role="alert"
                className="rounded-lg bg-negative-soft px-3 py-2 text-sm text-negative"
              >
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} className="mt-1 w-full" size="lg">
              {loading ? 'Skapar konto…' : 'Skapa konto'}
            </Button>
          </form>
        </div>
        <p className="mt-5 text-center text-sm text-muted-foreground">
          Har du redan ett konto?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Logga in
          </Link>
        </p>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Genom att skapa ett konto godkänner du{' '}
          <Link to="/terms" className="underline-offset-4 hover:text-foreground hover:underline">
            användarvillkoren
          </Link>{' '}
          och{' '}
          <Link to="/privacy" className="underline-offset-4 hover:text-foreground hover:underline">
            integritetspolicyn
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
